import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiClock, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './MaterialTimePicker.scss';

export interface MaterialTimePickerProps {
  value: string; // HH:mm (24h)
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

type Mode = 'hour' | 'minute';
type PopoverPlacement = 'top' | 'bottom';

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  placement: PopoverPlacement;
}

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function snapMinute(m: number): number {
  const r = Math.round(m / 5) * 5;
  if (r === 60) return 0;
  return Math.min(55, Math.max(0, r));
}

function parse24h(value: string): { hour: number; minute: number } {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return { hour: 0, minute: 0 };
  const [h, m] = value.split(':').map(Number);
  return {
    hour: Math.min(23, Math.max(0, h)),
    minute: Math.min(59, Math.max(0, m)),
  };
}

function to24h(displayHour: number, minute: number, isPM: boolean): string {
  let h = displayHour;
  if (isPM && displayHour !== 12) h += 12;
  else if (!isPM && displayHour === 12) h = 0;
  const hh = h.toString().padStart(2, '0');
  const mm = minute.toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatDisplay(hour24: number, minute: number): string {
  const h = hour24 % 12 || 12;
  const mm = minute.toString().padStart(2, '0');
  const ampm = hour24 < 12 ? 'AM' : 'PM';
  return `${h}:${mm} ${ampm}`;
}

function shortenLine(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  fromCenter: number,
  beforeEnd: number
) {
  const dx = ex - sx;
  const dy = ey - sy;
  const len = Math.hypot(dx, dy);
  if (len < 1) return { x1: sx, y1: sy, x2: ex, y2: ey };
  const ux = dx / len;
  const uy = dy / len;
  const x1 = sx + ux * fromCenter;
  const y1 = sy + uy * fromCenter;
  const x2 = ex - ux * beforeEnd;
  const y2 = ey - uy * beforeEnd;
  return { x1, y1, x2, y2 };
}

function MaterialTimePicker({
  value,
  onChange,
  placeholder = 'Select time',
  disabled = false,
}: MaterialTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('hour');
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    width: 320,
    placement: 'bottom',
  });

  const { hour: hour24, minute: minuteRaw } = parse24h(value);
  const displayHour = hour24 % 12 || 12;
  const isPM = hour24 >= 12;
  const minute = snapMinute(minuteRaw);

  const [localHour, setLocalHour] = useState(displayHour);
  const [localMinute, setLocalMinute] = useState(minute);
  const [localPM, setLocalPM] = useState(isPM);

  useEffect(() => {
    setLocalHour(displayHour);
    setLocalMinute(snapMinute(minuteRaw));
    setLocalPM(isPM);
  }, [value, displayHour, minuteRaw, isPM]);

  const handleInputClick = () => {
    if (disabled) return;
    setOpen(true);
    setMode('hour');
  };

  const handleCancel = () => {
    setLocalHour(displayHour);
    setLocalMinute(snapMinute(minuteRaw));
    setLocalPM(isPM);
    setOpen(false);
  };

  const handleOk = () => {
    onChange(to24h(localHour, localMinute, localPM));
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        rootRef.current &&
        popoverRef.current &&
        !rootRef.current.contains(e.target as Node) &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        handleCancel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleHourSelect = (h: number) => {
    setLocalHour(h);
    setMode('minute');
  };

  const handleMinuteSelect = (m: number) => {
    setLocalMinute(m);
  };

  const handleModePrev = () => {
    setMode((m) => (m === 'minute' ? 'hour' : 'minute'));
  };

  const handleModeNext = () => {
    setMode((m) => (m === 'hour' ? 'minute' : 'hour'));
  };

  const updatePopoverPosition = useCallback(() => {
    if (!rootRef.current || !popoverRef.current) return;

    const viewportPadding = 12;
    const popoverGap = 6;
    const triggerRect = rootRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const maxWidth = Math.max(280, window.innerWidth - viewportPadding * 2);
    const desiredWidth = Math.max(triggerRect.width, 320);
    const width = Math.min(desiredWidth, maxWidth);

    const spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
    const spaceAbove = triggerRect.top - viewportPadding;
    const shouldOpenAbove = spaceBelow < popoverRect.height + popoverGap && spaceAbove > spaceBelow;
    const placement: PopoverPlacement = shouldOpenAbove ? 'top' : 'bottom';

    const unclampedLeft = triggerRect.left;
    const left = Math.min(
      Math.max(viewportPadding, unclampedLeft),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
    );

    const preferredTop = shouldOpenAbove
      ? triggerRect.top - popoverRect.height - popoverGap
      : triggerRect.bottom + popoverGap;
    const top = Math.min(
      Math.max(viewportPadding, preferredTop),
      Math.max(viewportPadding, window.innerHeight - popoverRect.height - viewportPadding)
    );

    setPopoverPosition({ top, left, width, placement });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const frameId = window.requestAnimationFrame(updatePopoverPosition);
    const handleViewportChange = () => updatePopoverPosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, updatePopoverPosition]);

  const clockRadius = 68;
  const cx = 85;
  const cy = 85;

  const getPos = (index: number, total: number) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    return {
      x: cx + clockRadius * Math.cos(angle),
      y: cy + clockRadius * Math.sin(angle),
    };
  };

  const handTarget = useMemo(() => {
    if (mode === 'hour') {
      const idx = HOURS_12.indexOf(localHour);
      return getPos(idx >= 0 ? idx : 0, 12);
    }
    const idx = MINUTES.indexOf(localMinute);
    return getPos(idx >= 0 ? idx : 0, 12);
  }, [mode, localHour, localMinute]);

  const handLine = useMemo(
    () => shortenLine(cx, cy, handTarget.x, handTarget.y, 10, 22),
    [handTarget.x, handTarget.y]
  );

  const displayHourStr = String(localHour).padStart(2, '0');
  const displayMinuteStr = String(localMinute).padStart(2, '0');

  return (
    <div
      ref={rootRef}
      className={`material-time-picker-root ${open ? 'material-time-picker-root--open' : ''}`}
    >
      <div
        className={`material-time-picker-input ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleInputClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleInputClick();
          }
        }}
      >
        <span className="material-time-picker-input-text">
          {value ? (
            formatDisplay(hour24, minuteRaw)
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </span>
        <span className="material-time-picker-input-icon" aria-hidden>
          <FiClock size={18} />
        </span>
      </div>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className={`material-time-picker-popover material-time-picker-popover--${popoverPosition.placement}`}
            style={{
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              width: `${popoverPosition.width}px`,
            }}
          >
            <div className="material-time-picker-popover-inner">
              <div className="material-time-picker-hero">
                <div className="material-time-picker-hero-main">
                  <p className="material-time-picker-label">Select time</p>
                  <div className="material-time-picker-time-row">
                    <div className="material-time-picker-digital">
                      <span
                        className={`material-time-picker-digital-part ${
                          mode === 'hour' ? 'material-time-picker-digital-part--active' : ''
                        }`}
                      >
                        {displayHourStr}
                      </span>
                      <span className="material-time-picker-digital-colon">:</span>
                      <span
                        className={`material-time-picker-digital-part ${
                          mode === 'minute' ? 'material-time-picker-digital-part--active' : ''
                        }`}
                      >
                        {displayMinuteStr}
                      </span>
                    </div>
                    <div className="material-time-picker-meridiem-stack" role="group" aria-label="AM or PM">
                      <button
                        type="button"
                        className={`material-time-picker-meridiem-pill ${!localPM ? 'active' : ''}`}
                        onClick={() => setLocalPM(false)}
                      >
                        AM
                      </button>
                      <button
                        type="button"
                        className={`material-time-picker-meridiem-pill ${localPM ? 'active' : ''}`}
                        onClick={() => setLocalPM(true)}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>
                <div className="material-time-picker-mode-switch">
                  <button
                    type="button"
                    className="material-time-picker-arrow"
                    onClick={handleModePrev}
                    aria-label="Previous mode"
                  >
                    <FiChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    className="material-time-picker-arrow"
                    onClick={handleModeNext}
                    aria-label="Next mode"
                  >
                    <FiChevronRight size={18} />
                  </button>
                </div>
              </div>

              <div className="material-time-picker-clock-wrap">
                <div className="material-time-picker-clock">
                  <svg className="clock-svg" viewBox="0 0 170 170" xmlns="http://www.w3.org/2000/svg">
                    <circle
                      className="material-time-picker-clock-ring"
                      cx={cx}
                      cy={cy}
                      r={clockRadius + 2}
                      fill="none"
                      strokeWidth="1"
                    />
                    <line
                      x1={handLine.x1}
                      y1={handLine.y1}
                      x2={handLine.x2}
                      y2={handLine.y2}
                      className="material-time-picker-hand"
                      strokeLinecap="round"
                    />
                    <circle cx={cx} cy={cy} r={5} className="material-time-picker-hand-hub" />
                    {mode === 'hour' &&
                      HOURS_12.map((h, i) => {
                        const { x, y } = getPos(i, 12);
                        const selected = localHour === h;
                        return (
                          <g
                            key={h}
                            className={`clock-number ${selected ? 'selected' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleHourSelect(h)}
                          >
                            <circle
                              className="clock-number-bg"
                              cx={x}
                              cy={y}
                              r={18}
                              fill="transparent"
                            />
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="central"
                              className={`clock-number-text ${selected ? 'is-selected' : ''}`}
                              fontSize={15}
                            >
                              {h}
                            </text>
                          </g>
                        );
                      })}
                    {mode === 'minute' &&
                      MINUTES.map((m, i) => {
                        const { x, y } = getPos(i, 12);
                        const selected = localMinute === m;
                        return (
                          <g
                            key={m}
                            className={`clock-number ${selected ? 'selected' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleMinuteSelect(m)}
                          >
                            <circle
                              className="clock-number-bg"
                              cx={x}
                              cy={y}
                              r={18}
                              fill="transparent"
                            />
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="central"
                              className={`clock-number-text ${selected ? 'is-selected' : ''}`}
                              fontSize={13}
                            >
                              {m.toString().padStart(2, '0')}
                            </text>
                          </g>
                        );
                      })}
                  </svg>
                </div>
              </div>

              <div className="material-time-picker-actions">
                <button type="button" className="material-time-picker-btn cancel" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="button" className="material-time-picker-btn ok" onClick={handleOk}>
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default MaterialTimePicker;
